import { useEffect, useState } from "react";
import { endpoints } from "../api/endpoints";
import { useApi, useFreshness, useKeyedWrite } from "../api/hooks";
import type { ApplicationStatus, CertificateApplication, CertificateStatus, Checkout, Proposal } from "../api/types";
import { can, canStep } from "../auth/roles";
import { ActionDialog } from "../components/ActionDialog";
import { Badge, StatusBadge } from "../components/Badges";
import { Dialog } from "../components/Dialog";
import { safeHttpsUrl } from "../components/Files";
import { PendingProposalLine, ProposalDialog, ProposedResult } from "../components/FourEyes";
import {
  APPLICATION_KIND,
  APPLICATION_STATUS,
  APPLIED_AS,
  BILL_CANCELLATION_REASON,
  BILL_KIND,
  BILL_STATUS,
  CERTIFICATE_STATUS,
  OPEN_BILL_STATUSES,
  REINSPECTION_STATUS,
  REVOCATION_REASON,
  labelOf,
  optionsOf,
  statusOf,
} from "../components/labels";
import { Notice, ProblemAlert, QueryState, StaleNotice, UnknownOutcome } from "../components/ProblemAlert";
import { Tabs } from "../components/Tabs";
import { useFireContext } from "../context";
import { formatDate, formatDateTime, shortId } from "../format/datetime";
import { showMoney } from "../format/money";
import { personLabel, reasonError, Row, ScreenHeader, StandardsChecked, useConfigNames, useWardNames } from "./shared";
import { Button, IconButton, IconClose } from "@lcc/ui";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The bills of an application still open for payment (and so for a four-eyes cancellation). */
export function openBills(app: CertificateApplication) {
  return app.bills.filter((b) => (OPEN_BILL_STATUSES as readonly string[]).includes(b.status));
}

/**
 * The owner's ZamPay checkout for the open bill, requested by a fire officer on their behalf. Only an https checkout
 * link is shown (the page is the payment provider's; FER never handles money).
 */
function CheckoutDialog({ app, onClose }: { app: CertificateApplication; onClose: () => void }) {
  const write = useKeyedWrite((api, _v: unknown, key) => endpoints.checkoutFee(api, app.application_id, key), `checkoutCertificateFee:${app.application_id}`);
  const [checkout, setCheckout] = useState<Checkout | null>(null);
  const url = checkout ? safeHttpsUrl(checkout.checkout_url) : null;
  const get = () => write.mutate(undefined, { onSuccess: setCheckout });
  return (
    <Dialog title={`Payment page for ${app.reference}`} onClose={onClose}>
      <div className="fer-form">
        <p>The owner pays the council's bill through ZamPay. Show or send them the payment page.</p>
        {checkout ? (
          url ? (
            <p role="status">
              <a href={url} target="_blank" rel="noopener noreferrer">
                Open the payment page
              </a>{" "}
              (valid until {formatDateTime(checkout.expires_at)})
            </p>
          ) : (
            <Notice tone="warning">The payment service returned a link this screen does not open (it is not a secure https link). Try again or ask support.</Notice>
          )
        ) : (
          <>
            <ProblemAlert error={write.error} />
            <UnknownOutcome write={write} retry={get} />
          </>
        )}
        <div className="fer-actions">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          {!checkout && (
            <Button disabled={write.isPending} onClick={get}>
              Get the payment page
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
}

type AppDialog = { kind: "checkout" } | { kind: "cancelBill" } | { kind: "reinspect" } | { kind: "proposal"; p: Proposal } | null;

function ApplicationDetail({ applicationId, onClose }: { applicationId: string; onClose?: () => void }) {
  const { principal, now, go } = useFireContext();
  const names = useConfigNames();
  const wardName = useWardNames();
  const q = useApi(["application", applicationId], (api) => endpoints.getApplication(api, applicationId));
  const approvals = useApi(["approvals", "all", "pending"], (api) => endpoints.listApprovals(api, { mine: "all", status: "pending" }), { enabled: can(principal, "viewApprovals") });
  const [dialog, setDialog] = useState<AppDialog>(null);
  const [proposed, setProposed] = useState<Proposal | null>(null);
  const a = q.data;
  const fresh = useFreshness(q);
  if (!a) return <QueryState isLoading={q.isLoading} error={q.error} label="the application" />;
  const open = openBills(a);
  const pending = (approvals.data?.items ?? []).filter((p) => p.subject_id === a.application_id);
  const reinspectOpen = a.reinspections.some((r) => r.status === "awaiting_payment" || r.status === "paid");
  const windowOpen = !!a.reinspection_until && Date.parse(a.reinspection_until) > now().getTime();
  return (
    <section className="fer-panel fer-record" aria-label={`Application ${a.reference}`}>
      <div className="lcc-detail__head">
        <div className="lcc-detail__titles">
          <h3 className="lcc-detail__title">{a.reference}: {a.premises_name ?? shortId(a.premises_id)}</h3>
          <span className="fer-badges fer-record__status">
            <StatusBadge status={statusOf(APPLICATION_STATUS, a.status)} />
          </span>
        </div>
        {onClose && <IconButton label="Close" size="sm" icon={<IconClose size={16} />} onClick={onClose} />}
      </div>
      <StaleNotice {...fresh} error={q.error} what="application" />
      {a.payment_reversed && <Badge tone="warning" label="A payment was reversed after the inspection (a debt)" />}
      {a.pass_awaiting_payment && <Badge tone="warning" label="Passed: the certificate waits for payment" />}
      {proposed && <ProposedResult proposal={proposed} />}
      {pending.map((p) => (
        <PendingProposalLine key={p.proposal_id} proposal={p} onOpen={() => setDialog({ kind: "proposal", p })} />
      ))}
      <dl className="fer-dl">
        <Row term="Kind">{labelOf(APPLICATION_KIND, a.kind)}</Row>
        <Row term="Ward">{wardName(a.ward_id)}</Row>
        <Row term="Owner">{shortId(a.owner_party_id)}</Row>
        <Row term="Risk class">{names.riskClass(a.risk_class)}</Row>
        <Row term="Fee">{showMoney(a.fee)}</Row>
        <Row term="Applied">
          {formatDateTime(a.applied_at)} by {labelOf(APPLIED_AS, a.applied_as)} ({personLabel(a.applied_by, principal.partyId)})
        </Row>
        <Row term="Pay by">{formatDateTime(a.pay_by)}</Row>
        {a.paid_at && <Row term="Paid">{formatDateTime(a.paid_at)}</Row>}
        {a.reinspection_until && <Row term="Re-inspection may be asked until">{formatDateTime(a.reinspection_until)}</Row>}
        {a.ended_at && <Row term="Ended">{formatDateTime(a.ended_at)}</Row>}
      </dl>
      <div className="fer-actions fer-actions--start" role="group" aria-label="Application actions">
        {can(principal, "checkoutFee") && open.length > 0 && (
          <Button onClick={() => setDialog({ kind: "checkout" })}>
            Payment page for the owner
          </Button>
        )}
        {canStep(principal, "bill_cancellation", "propose") && open.length > 0 && (
          <Button variant="secondary" onClick={() => setDialog({ kind: "cancelBill" })}>
            Propose cancelling a bill
          </Button>
        )}
        {can(principal, "requestReinspection") && a.status === "failed" && windowOpen && !reinspectOpen && (
          <Button variant="secondary" onClick={() => setDialog({ kind: "reinspect" })}>
            Ask for a re-inspection for the owner
          </Button>
        )}
        {a.certificate_id && can(principal, "viewCertificates") && (
          <Button variant="secondary" onClick={() => go("certificates", { certificateId: a.certificate_id! })}>
            Open the certificate
          </Button>
        )}
      </div>
      <table className="lcc-table" aria-label="Bills">
        <thead>
          <tr>
            <th scope="col">Bill</th>
            <th scope="col">For</th>
            <th scope="col" className="fer-num">
              Amount
            </th>
            <th scope="col">Due</th>
            <th scope="col">Status</th>
            <th scope="col">Receipt</th>
          </tr>
        </thead>
        <tbody>
          {a.bills.map((b) => (
            <tr key={`${b.source_id}`}>
              <th scope="row">{b.source_id}</th>
              <td>{labelOf(BILL_KIND, b.kind)}</td>
              <td className="fer-num">{showMoney(b.amount)}</td>
              <td>{b.due_at ? formatDateTime(b.due_at) : "No deadline (a debt)"}</td>
              <td>
                <StatusBadge status={statusOf(BILL_STATUS, b.status)} />
              </td>
              <td>{b.receipt_number ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {a.reinspections.length > 0 && (
        <table className="lcc-table" aria-label="Re-inspections">
          <thead>
            <tr>
              <th scope="col">Asked</th>
              <th scope="col" className="fer-num">
                Fee
              </th>
              <th scope="col">Pay by</th>
              <th scope="col">Status</th>
              <th scope="col">Note</th>
            </tr>
          </thead>
          <tbody>
            {a.reinspections.map((r) => (
              <tr key={r.reinspection_id}>
                <th scope="row">{formatDateTime(r.requested_at)}</th>
                <td className="fer-num">{showMoney(r.fee)}</td>
                <td>{formatDateTime(r.pay_by)}</td>
                <td>
                  <StatusBadge status={statusOf(REINSPECTION_STATUS, r.status)} />
                </td>
                <td>{r.note ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {dialog?.kind === "checkout" && <CheckoutDialog app={a} onClose={() => setDialog(null)} />}
      {dialog?.kind === "proposal" && <ProposalDialog proposal={dialog.p} onClose={() => setDialog(null)} />}
      {dialog?.kind === "cancelBill" && (
        <ActionDialog
          title={`Propose cancelling a bill of ${a.reference}`}
          intent={`cancelCertificateBill:${a.application_id}`}
          intro={<p>The chief or a finance officer confirms. A cancelled application fee cancels the application; a written-off debt may issue a waiting certificate.</p>}
          initial={{ bill_source_id: open[0]?.source_id ?? "" }}
          fields={[
            { name: "bill_source_id", label: "Bill", kind: "select", required: true, options: open.map((b) => [b.source_id, `${b.source_id} – ${labelOf(BILL_KIND, b.kind)}, ${showMoney(b.amount)}`] as const) },
            { name: "reason_category", label: "Why", kind: "select", required: true, options: optionsOf(BILL_CANCELLATION_REASON) },
            { name: "reason", label: "Details (at least 3 characters)", kind: "textarea", required: true, maxLength: 1000 },
          ]}
          submitLabel="Propose"
          build={(v) => {
            const e = reasonError(v.reason ?? "");
            return e ? { error: e } : { body: { action: "propose", bill_source_id: v.bill_source_id, reason_category: v.reason_category, reason: v.reason.trim() } };
          }}
          write={(api, body, key) => endpoints.cancelBill(api, a.application_id, body as never, key)}
          onDone={(p) => setProposed(p as Proposal)}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "reinspect" && (
        <ActionDialog
          title={`Ask for a re-inspection of ${a.reference}`}
          intent={`requestReinspection:${a.application_id}`}
          intro={<p>The owner is billed the re-inspection fee of the premises' risk class; unpaid, the request lapses.</p>}
          fields={[{ name: "note", label: "What the owner has fixed", kind: "textarea", maxLength: 1000 }]}
          submitLabel="Ask for the re-inspection"
          build={(v) => ({ body: v.note?.trim() ? { note: v.note.trim() } : {} })}
          write={(api, body, key) => endpoints.requestReinspection(api, a.application_id, body as never, key)}
          onClose={() => setDialog(null)}
        />
      )}
    </section>
  );
}

function ApplicationsTab({ focusId }: { focusId?: string }) {
  const wardName = useWardNames();
  const [status, setStatus] = useState<ApplicationStatus | "">("");
  const [selected, setSelected] = useState<string | null>(focusId ?? null);
  const q = useApi(["applications", status], (api) => endpoints.listApplications(api, { status: status ? [status] : undefined }), { keepPrevious: true });
  const rows = q.data ?? [];
  return (
    <>
      <div className="fer-toolbar">
        <label className="fer-field fer-field--inline">
          <span>Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value as ApplicationStatus | "")}>
            <option value="">All</option>
            {Object.entries(APPLICATION_STATUS).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <QueryState isLoading={q.isLoading} error={q.error} label="applications" />
      {q.data && rows.length === 0 && <p className="fer-muted">No application matches.</p>}
      <div className={`fer-split${selected ? " fer-split--open" : ""}`}>
        <div className="fer-split__main">
          {rows.length > 0 && (
            <table className="lcc-table" aria-label={`Certificate applications (${rows.length})`}>
              <thead>
                <tr>
                  <th scope="col">Application</th>
                  <th scope="col">Premises</th>
                  <th scope="col">Ward</th>
                  <th scope="col" className="fer-num">
                    Fee
                  </th>
                  <th scope="col">Status</th>
                  <th scope="col">
                    <span className="lcc-sr-only">Open</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.application_id} className={a.application_id === selected ? "fer-row--selected" : undefined}>
                    <th scope="row">{a.reference}</th>
                    <td>{a.premises_name ?? shortId(a.premises_id)}</td>
                    <td>{wardName(a.ward_id)}</td>
                    <td className="fer-num">{showMoney(a.fee)}</td>
                    <td>
                      <StatusBadge status={statusOf(APPLICATION_STATUS, a.status)} />
                    </td>
                    <td>
                      <Button variant="secondary" size="sm" onClick={() => setSelected(a.application_id)} aria-label={`Open ${a.reference}`}>
                        Open
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {selected && (
          <div className="fer-detailpane">
            <ApplicationDetail key={selected} applicationId={selected} onClose={() => setSelected(null)} />
          </div>
        )}
      </div>
    </>
  );
}

function CertificateDetail({ certificateId, onClose }: { certificateId: string; onClose?: () => void }) {
  const { principal } = useFireContext();
  const names = useConfigNames();
  const wardName = useWardNames();
  const q = useApi(["certificate", certificateId], (api) => endpoints.getCertificate(api, certificateId));
  const approvals = useApi(["approvals", "all", "pending"], (api) => endpoints.listApprovals(api, { mine: "all", status: "pending" }), { enabled: can(principal, "viewApprovals") });
  const [dialog, setDialog] = useState<{ kind: "revoke" } | { kind: "proposal"; p: Proposal } | null>(null);
  const [proposed, setProposed] = useState<Proposal | null>(null);
  const c = q.data;
  const fresh = useFreshness(q);
  if (!c) return <QueryState isLoading={q.isLoading} error={q.error} label="the certificate" />;
  const pending = (approvals.data?.items ?? []).filter((p) => p.subject_id === c.certificate_id);
  return (
    <section className="fer-panel fer-record" aria-label={`Certificate ${c.number}`}>
      <div className="lcc-detail__head">
        <div className="lcc-detail__titles">
          <h3 className="lcc-detail__title">{c.number}: {c.premises_name}</h3>
          <span className="fer-badges fer-record__status">
            <StatusBadge status={statusOf(CERTIFICATE_STATUS, c.status)} />
          </span>
        </div>
        {onClose && <IconButton label="Close" size="sm" icon={<IconClose size={16} />} onClick={onClose} />}
      </div>
      <StaleNotice {...fresh} error={q.error} what="certificate" />
      {c.payment_reversed && <Badge tone="warning" label="Its fee payment was reversed" />}
      {proposed && <ProposedResult proposal={proposed} />}
      {pending.map((p) => (
        <PendingProposalLine key={p.proposal_id} proposal={p} onOpen={() => setDialog({ kind: "proposal", p })} />
      ))}
      <dl className="fer-dl">
        <Row term="Valid">
          {formatDate(c.valid_from)} to {formatDate(c.valid_to)} (Lusaka days, inclusive)
        </Row>
        <Row term="Verification code">{c.verification_code ?? <span className="fer-muted">Not shown to you</span>}</Row>
        <Row term="Ward">{wardName(c.ward_id)}</Row>
        <Row term="Occupancy and class">
          {names.occupancy(c.occupancy_type)}, {names.riskClass(c.risk_class)}
        </Row>
        <Row term="Issued">
          {formatDateTime(c.issued_at)} after the inspection by {personLabel(c.issued_by, principal.partyId)}
        </Row>
        <StandardsChecked staff={names.staff} occupancyType={c.occupancy_type} />
        {c.renewal_of && <Row term="Renews">{shortId(c.renewal_of)}</Row>}
        {c.revoked_at && (
          <Row term="Revoked">
            {formatDateTime(c.revoked_at)}: {labelOf(REVOCATION_REASON, c.revocation_reason_category)}
          </Row>
        )}
      </dl>
      {c.status === "valid" && canStep(principal, "certificate_revocation", "propose") && (
        <div className="fer-actions fer-actions--start">
          <Button onClick={() => setDialog({ kind: "revoke" })}>
            Propose revoking the certificate
          </Button>
        </div>
      )}
      {dialog?.kind === "proposal" && <ProposalDialog proposal={dialog.p} onClose={() => setDialog(null)} />}
      {dialog?.kind === "revoke" && (
        <ActionDialog
          title={`Propose revoking ${c.number}`}
          intent={`revokeCertificate:${c.certificate_id}`}
          intro={<p>Another fire officer of the ward or the chief confirms. The certificates it renews and its renewals are revoked with it. A revoked certificate is never reinstated: the owner applies again.</p>}
          fields={[
            { name: "reason_category", label: "Why", kind: "select", required: true, options: optionsOf(REVOCATION_REASON) },
            { name: "reason", label: "Details (at least 3 characters)", kind: "textarea", required: true, maxLength: 2000 },
            { name: "inspection_id", label: "The inspection that found it (id, optional)", maxLength: 36 },
          ]}
          submitLabel="Propose"
          build={(v) => {
            const e = reasonError(v.reason ?? "");
            if (e) return { error: e };
            if (v.inspection_id?.trim() && !UUID.test(v.inspection_id.trim())) return { error: "The inspection id is a UUID (36 characters)." };
            return { body: { action: "propose", reason_category: v.reason_category, reason: v.reason.trim(), inspection_id: v.inspection_id?.trim() || null } };
          }}
          write={(api, body, key) => endpoints.revokeCertificate(api, c.certificate_id, body as never, key)}
          onDone={(p) => setProposed(p as Proposal)}
          onClose={() => setDialog(null)}
        />
      )}
    </section>
  );
}

function CertificatesTab({ focusId }: { focusId?: string }) {
  const wardName = useWardNames();
  const [status, setStatus] = useState<CertificateStatus | "">("");
  const [expiring, setExpiring] = useState("");
  const [selected, setSelected] = useState<string | null>(focusId ?? null);
  const days = /^\d{1,3}$/.test(expiring) && Number(expiring) >= 1 && Number(expiring) <= 366 ? Number(expiring) : undefined;
  const q = useApi(["certificates", status, days ?? null], (api) => endpoints.listCertificates(api, { status: status || undefined, expiringWithinDays: days }), { keepPrevious: true });
  const rows = q.data ?? [];
  return (
    <>
      <div className="fer-toolbar">
        <label className="fer-field fer-field--inline">
          <span>Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value as CertificateStatus | "")}>
            <option value="">All</option>
            {Object.entries(CERTIFICATE_STATUS).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
        </label>
        <label className="fer-field fer-field--inline">
          <span>Expiring within (days, 1-366)</span>
          <input inputMode="numeric" value={expiring} maxLength={3} onChange={(e) => setExpiring(e.target.value)} />
        </label>
      </div>
      <QueryState isLoading={q.isLoading} error={q.error} label="certificates" />
      {q.data && rows.length === 0 && <p className="fer-muted">No certificate matches.</p>}
      <div className={`fer-split${selected ? " fer-split--open" : ""}`}>
        <div className="fer-split__main">
          {rows.length > 0 && (
            <table className="lcc-table" aria-label={`Certificates (${rows.length})`}>
              <thead>
                <tr>
                  <th scope="col">Number</th>
                  <th scope="col">Premises</th>
                  <th scope="col">Ward</th>
                  <th scope="col">Valid to</th>
                  <th scope="col">Status</th>
                  <th scope="col">
                    <span className="lcc-sr-only">Open</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.certificate_id} className={c.certificate_id === selected ? "fer-row--selected" : undefined}>
                    <th scope="row">{c.number}</th>
                    <td>{c.premises_name}</td>
                    <td>{wardName(c.ward_id)}</td>
                    <td>{formatDate(c.valid_to)}</td>
                    <td>
                      <StatusBadge status={statusOf(CERTIFICATE_STATUS, c.status)} />
                    </td>
                    <td>
                      <Button variant="secondary" size="sm" onClick={() => setSelected(c.certificate_id)} aria-label={`Open ${c.number}`}>
                        Open
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {selected && (
          <div className="fer-detailpane">
            <CertificateDetail key={selected} certificateId={selected} onClose={() => setSelected(null)} />
          </div>
        )}
      </div>
    </>
  );
}

type Tab = "applications" | "certificates";

/** Certificate applications with their bills (money in Kwacha), re-inspections and four-eyes bill cancellations; certificates and their four-eyes revocation. */
export function CertificatesScreen() {
  const { principal, focus } = useFireContext();
  const tabs = [
    ...(can(principal, "viewApplications") ? [{ key: "applications" as const, label: "Applications and fees" }] : []),
    ...(can(principal, "viewCertificates") ? [{ key: "certificates" as const, label: "Certificates" }] : []),
  ];
  const [tab, setTab] = useState<Tab>(focus.certificateId && can(principal, "viewCertificates") ? "certificates" : (tabs[0]?.key ?? "applications"));
  useEffect(() => {
    if (focus.certificateId && can(principal, "viewCertificates")) setTab("certificates");
    else if (focus.applicationId) setTab("applications");
  }, [focus.certificateId, focus.applicationId, principal]);
  const current = tabs.some((t) => t.key === tab) ? tab : (tabs[0]?.key ?? "applications");
  return (
    <div className="fer-screen">
      <ScreenHeader title="Certificates and fees" />
      <Tabs label="Certificates" tabs={tabs} current={current} onChange={setTab}>
        {current === "applications" ? <ApplicationsTab key={focus.applicationId ?? ""} focusId={focus.applicationId} /> : <CertificatesTab key={focus.certificateId ?? ""} focusId={focus.certificateId} />}
      </Tabs>
    </div>
  );
}
